import {Box, Button, Card, CardContent, Dialog, DialogTitle, Grid, TextField, Typography} from '@mui/material';
import {Build, CameraAlt, Delete, Favorite, LocalLaundryService, LocalShipping, MoreHoriz, SvgIconComponent} from '@mui/icons-material';
import {useForm} from 'react-hook-form';
import {useState} from 'react';
import {BrandItemProblemInput} from '../../../Queries';
import {
   BRAND_ITEM_REQUEST_ACTIONS,
   BrandItemRequestActionCode,
   buildBrandItemActionProblem
} from './BrandItemRequestActionRules';

interface RequestActionProps {
   open: boolean;
   onClose: () => void;
   onSubmitRequest: (problem: BrandItemProblemInput) => Promise<void>;
}

const REQUEST_ACTION_ICONS: Record<BrandItemRequestActionCode, SvgIconComponent> = {
   RETAKE_PHOTOS: CameraAlt,
   REPAIR: Build,
   WASH: LocalLaundryService,
   DISCARD: Delete,
   DONATE: Favorite,
   SEND_TO_BRAND: LocalShipping,
   PAUSE: LocalShipping,
   OTHER: MoreHoriz
};

interface ActionFormData {
   additionalDetails: string;
}

/** Presents the existing modal Request Action flow using the shared action rules. */
export const BrandItemRequestAction = ({ open, onClose, onSubmitRequest }: RequestActionProps) => {
   const { register, watch, reset } = useForm<ActionFormData>({
      defaultValues: {
         additionalDetails: '',
      }
   });

   const [selectedAction, setSelectedAction] = useState<BrandItemRequestActionCode | null>(null);
   const additionalDetails = watch('additionalDetails');

   const handleActionSelect = (code: BrandItemRequestActionCode) => {
      setSelectedAction(code);
   };

   const handleSubmit = async () => {
      if (!selectedAction) return;

      await onSubmitRequest(buildBrandItemActionProblem(selectedAction, additionalDetails));
      handleClose();
   };

   const handleClose = () => {
      setSelectedAction(null);
      reset();
      onClose();
   };

   return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
         <DialogTitle>Request Action</DialogTitle>
         <Grid container spacing={2} sx={{ p: 2 }}>
            {BRAND_ITEM_REQUEST_ACTIONS.map((action) => {
               const ActionIcon = REQUEST_ACTION_ICONS[action.code];
               return <Grid item xs={12} sm={6} key={action.code}>
                  <Card
                     sx={{
                        cursor: 'pointer',
                        backgroundColor: selectedAction === action.code ? 'action.selected' : 'background.paper',
                        transition: 'background-color 0.2s',
                        '&:hover': {
                           backgroundColor: selectedAction === action.code
                              ? 'action.selected'
                              : 'action.hover'
                        }
                     }}
                     onClick={() => handleActionSelect(action.code)}
                  >
                     <CardContent sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                     }}>
                        <ActionIcon color="primary" />
                        <div>
                           <Typography variant="h6" component="div">
                              {action.title}
                           </Typography>
                           <Typography variant="body2" color="text.secondary">
                              {action.description}
                           </Typography>
                        </div>
                     </CardContent>
                  </Card>
               </Grid>;
            })}

            <Grid item xs={12}>
               <Box sx={{ mt: 2 }}>
                  <TextField
                     fullWidth
                     label="Additional details (optional)"
                     {...register('additionalDetails')}
                     multiline
                     rows={2}
                     placeholder="Add any specific details about your request..."
                  />
               </Box>
            </Grid>

            <Grid item xs={12}>
               <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                  <Button onClick={handleClose}>Cancel</Button>
                  <Button
                     variant="contained"
                     onClick={handleSubmit}
                     disabled={!selectedAction}
                  >
                     Submit Request
                  </Button>
               </Box>
            </Grid>
         </Grid>
      </Dialog>
   );
};
